import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ShieldCheck,
  Lock,
  Radio,
  Crown,
  Users,
  Languages,
  Film,
  Clock,
  Pencil,
  Trash2,
  Loader2,
  ToggleLeft,
  ToggleRight,
  Sparkles,
  FileText,
  DollarSign,
  BarChart3,
  Gift,
  Play,
} from "lucide-react";
import { api } from "@/lib/api";
import { shieldBlockedMessage } from "@/lib/shield";
import { LinkChips } from "@/components/common/LinkChips";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/common/Avatar";
import { TopConnectorCrown } from "@/components/common/TopConnectorCrown";
import { BloqEmoji } from "@/components/bloqs/BloqRows";
import { VerifiedCheck, TrustBand, GatePill } from "@/components/common/TrustBadge";
import { timeAgo } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BloqBoostSection } from "@/components/bloqs/BloqBoostCard";
import { TokenGateSection } from "@/components/bloqs/TokenGateSection";
import { EventsSection } from "@/components/bloqs/EventsSection";
import { MICROTX_ENABLED } from "@/config";

const ROLE_STYLE = {
  owner: "border-bloq-purple/40 bg-bloq-purple/15 text-bloq-purple",
  admin: "border-bloq-blue/40 bg-bloq-blue/15 text-bloq-blue",
  mod: "border-bloq-mint/40 bg-bloq-mint/15 text-bloq-mint",
};

function InfoTile({ icon: Icon, label, value, accent }) {
  return (
    <div className="flex items-center gap-2.5 rounded-2xl border border-white/8 bg-bloq-s2 px-3.5 py-3">
      <span className={cn("flex h-9 w-9 items-center justify-center rounded-xl bg-white/5", accent)}>
        <Icon className="h-4 w-4" />
      </span>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-bloq-muted">{label}</div>
        <div className="text-sm font-semibold">{value}</div>
      </div>
    </div>
  );
}

function EditBloqDialog({ bloq, open, onOpenChange, onSaved }) {
  const [name, setName] = useState(bloq.name || "");
  const [emoji, setEmoji] = useState(bloq.emoji || "");
  const [description, setDescription] = useState(bloq.description || "");
  const [minScore, setMinScore] = useState(bloq.gate?.min_score ?? 0);
  const [requireVerified, setRequireVerified] = useState(!!bloq.gate?.require_verified);
  const [busy, setBusy] = useState(false);
  const [nftChain, setNftChain] = useState("ethereum");
  const [nftContract, setNftContract] = useState("");
  const [nftTokenId, setNftTokenId] = useState("");
  const [nftBusy, setNftBusy] = useState(false);

  const setNftIcon = async () => {
    if (!nftContract.trim() || !nftTokenId.trim()) return toast.error("Enter the contract address and token ID.");
    setNftBusy(true);
    try {
      await api.setBloqNftIcon(bloq.id, { chain: nftChain, contract: nftContract.trim(), token_id: nftTokenId.trim() });
      toast.success("NFT set as this BLOQ's icon");
      onSaved?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" && d) || d?.message || "Couldn't set the NFT icon (owner must hold it in a verified wallet).");
    } finally {
      setNftBusy(false);
    }
  };

  const save = async () => {
    if (name.trim().length < 3) return toast.error("Name must be at least 3 characters.");
    setBusy(true);
    try {
      await api.updateBloq(bloq.id, {
        name: name.trim(),
        emoji: emoji.trim() || undefined,
        description: description.trim(),
        min_score: Number(minScore) || 0,
        require_verified: requireVerified,
      });
      toast.success("BLOQ updated");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(shieldBlockedMessage(e) || e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't update bloq");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-bloq-s1 text-white" data-testid="edit-bloq-dialog">
        <DialogHeader><DialogTitle className="font-heading">Edit bloq</DialogTitle></DialogHeader>
        <DialogDescription className="text-sm text-bloq-muted">Owner-only. Token gates can't be changed after creation.</DialogDescription>
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-3">
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-bloq-muted">Emoji</Label><Input value={emoji} onChange={(e) => setEmoji(e.target.value)} maxLength={4} className="h-11 rounded-xl border-white/12 bg-bloq-bg text-center text-xl" data-testid="edit-bloq-emoji" /></div>
            <div className="col-span-3 space-y-1.5"><Label className="text-xs uppercase tracking-wider text-bloq-muted">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} className="h-11 rounded-xl border-white/12 bg-bloq-bg" data-testid="edit-bloq-name" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-bloq-muted">Description</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} className="h-11 rounded-xl border-white/12 bg-bloq-bg" data-testid="edit-bloq-description" /></div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-bloq-muted">Minimum trust score: {minScore}</Label>
            <input type="range" min={0} max={100} value={minScore} onChange={(e) => setMinScore(e.target.value)} className="w-full accent-bloq-mint" data-testid="edit-bloq-minscore" />
          </div>
          <button onClick={() => setRequireVerified((v) => !v)} className="flex w-full items-center justify-between rounded-xl border border-white/8 bg-bloq-s2 px-4 py-3" data-testid="edit-bloq-verified-toggle">
            <span className="text-sm">Require a verified badge</span>
            {requireVerified ? <ToggleRight className="h-6 w-6 text-bloq-mint" /> : <ToggleLeft className="h-6 w-6 text-bloq-muted" />}
          </button>

          <div className="space-y-2 rounded-xl border border-bloq-purple/25 bg-bloq-purple/[0.06] p-3" data-testid="edit-bloq-nft-icon">
            <Label className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-bloq-purple"><Sparkles className="h-3.5 w-3.5" /> Set an NFT you own as the icon</Label>
            <div className="grid grid-cols-3 gap-2">
              <select value={nftChain} onChange={(e) => setNftChain(e.target.value)} className="h-10 rounded-lg border border-white/12 bg-bloq-bg px-2 text-sm text-white" data-testid="edit-bloq-nft-chain">
                <option value="ethereum">Ethereum</option>
                <option value="base">Base</option>
                <option value="polygon">Polygon</option>
                <option value="solana">Solana</option>
              </select>
              <Input value={nftContract} onChange={(e) => setNftContract(e.target.value)} placeholder="Contract 0x…" className="col-span-2 h-10 rounded-lg border-white/12 bg-bloq-bg text-sm" data-testid="edit-bloq-nft-contract" />
            </div>
            <div className="flex gap-2">
              <Input value={nftTokenId} onChange={(e) => setNftTokenId(e.target.value)} placeholder="Token ID" className="h-10 flex-1 rounded-lg border-white/12 bg-bloq-bg text-sm" data-testid="edit-bloq-nft-tokenid" />
              <Button onClick={setNftIcon} disabled={nftBusy} className="h-10 shrink-0 rounded-lg bg-bloq-purple px-4 text-sm font-bold text-white hover:opacity-90" data-testid="edit-bloq-nft-submit">
                {nftBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set icon"}
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} className="w-full rounded-full bloq-gradient font-bold text-bloq-bg" data-testid="edit-bloq-submit">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save changes"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function fmtDuration(sec) {
  const s = Math.round(Number(sec) || 0);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

function RecordingRow({ rec, index, autoplay }) {
  const [showRecap, setShowRecap] = useState(false);
  const [showTx, setShowTx] = useState(false);
  const [dl, setDl] = useState(false);
  const [listens, setListens] = useState(rec.replay_listens ?? 0);
  const listenedRef = useRef(false);
  const audioRef = useRef(null);
  const title = rec.room_title || rec.title || "Room recording";
  const duration = rec.duration_s ?? rec.duration;
  const ready = rec.status === "ready" && rec.url;
  const recapReady = rec.recap_status === "ready" || !!rec.summary;

  // cc_16 replay card: seek to the top moment + start playback when deep-linked.
  useEffect(() => {
    if (!autoplay || !ready || !audioRef.current) return;
    const a = audioRef.current;
    const seek = () => { try { a.currentTime = autoplay.t || 0; a.play().catch(() => {}); } catch { /* not seekable yet */ } };
    if (a.readyState >= 1) seek(); else a.addEventListener("loadedmetadata", seek, { once: true });
    a.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, [autoplay, ready]);

  // cc_17: count this member once real playback starts (server $addToSet → idempotent).
  const onPlay = async () => {
    if (listenedRef.current || !rec.id) return;
    listenedRef.current = true;
    try { const r = await api.audioListened(rec.id); if (typeof r?.listens === "number") setListens(r.listens); } catch { /* best-effort */ }
  };

  const downloadTranscript = async () => {
    setDl(true);
    try {
      const data = await api.recordingTranscript(rec.id); // { filename, transcript, summary }
      const text = data?.transcript || rec.transcript || "";
      const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = data?.filename || `${title.replace(/\s+/g, "_")}-transcript.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const code = e?.response?.data?.detail?.code || e?.response?.data?.code;
      const status = e?.response?.status;
      if (code === "upgrade_required" || status === 402) toast.error("Downloadable transcripts are a Circle Pro perk.");
      else if (status === 403) toast.error("Only bloq members can access transcripts.");
      else if (status === 404) toast.error("No transcript for this recording.");
      else toast.error("Couldn't download transcript.");
    } finally {
      setDl(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/8 bg-bloq-s2 p-3" data-testid={`recording-${index}`}>
      <div className="flex items-center gap-2">
        <Film className="h-4 w-4 shrink-0 text-bloq-purple" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{title}</div>
          <div className="text-[11px] text-bloq-muted">
            {timeAgo(rec.started_at || rec.created_at)}
            {duration ? ` · ${fmtDuration(duration)}` : ""}
            {rec.started_by ? ` · ${rec.started_by}` : ""}
          </div>
        </div>
        {!ready && (
          <span className="rounded-full bg-white/8 px-2 py-0.5 text-[10px] uppercase tracking-wide text-bloq-muted" data-testid={`recording-status-${index}`}>
            {rec.status || "processing"}
          </span>
        )}
      </div>

      {ready && (
        <>
          <audio ref={audioRef} controls preload={autoplay ? "metadata" : "none"} src={rec.url} onPlay={onPlay} className="mt-2.5 h-9 w-full" data-testid={`recording-audio-${index}`} />
          {listens > 0 && (
            <div className="mt-1.5 flex items-center gap-1 text-[11px] text-bloq-muted" data-testid={`recording-listens-${index}`}>
              <Play className="h-3 w-3 text-bloq-mint" /> {listens} {listens === 1 ? "member" : "members"} caught the replay
            </div>
          )}
        </>
      )}

      {(recapReady || rec.recap_status) && (
        <div className="mt-2.5">
          {recapReady ? (
            <>
              <button
                onClick={() => setShowRecap((v) => !v)}
                className="flex items-center gap-1.5 text-xs font-semibold text-bloq-mint hover:opacity-80"
                data-testid={`recording-recap-toggle-${index}`}
              >
                <Sparkles className="h-3.5 w-3.5" /> {showRecap ? "Hide recap" : "AI recap"}
              </button>
              {showRecap && (
                <p className="mt-1.5 whitespace-pre-wrap text-xs leading-relaxed text-white/80" data-testid={`recording-recap-${index}`}>
                  {rec.summary || "No recap available."}
                </p>
              )}
            </>
          ) : (
            <span className="flex items-center gap-1.5 text-xs text-bloq-muted">
              <Loader2 className="h-3 w-3 animate-spin" /> Recap {rec.recap_status || "processing"}…
            </span>
          )}
        </div>
      )}

      {rec.transcript && (
        <div className="mt-2">
          <button onClick={() => setShowTx((v) => !v)} className="flex items-center gap-1.5 text-xs font-semibold text-bloq-muted hover:text-white" data-testid={`recording-transcript-toggle-${index}`}>
            <FileText className="h-3.5 w-3.5" /> {showTx ? "Hide transcript" : "View transcript"}
          </button>
          {showTx && (
            <div className="mt-1.5" data-testid={`recording-transcript-${index}`}>
              <p className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-lg bg-bloq-bg/60 p-2 text-xs leading-relaxed text-white/75">
                {rec.transcript}
              </p>
              <button onClick={downloadTranscript} disabled={dl} className="mt-1.5 flex items-center gap-1.5 text-xs font-semibold text-bloq-mint hover:opacity-80 disabled:opacity-50" data-testid={`recording-transcript-download-${index}`}>
                {dl ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} Download .txt (Circle Pro)
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Top Supporters — the bloq's most generous tippers this week (mobile-owned tip leaderboard,
// scope=bloq:{id}). Social proof that makes supporting a creator's bloq visible + rewarding.
function BloqSupportersSection({ bloq, open }) {
  const { data } = useQuery({
    queryKey: ["bloq-supporters", bloq.id],
    queryFn: () => api.tipLeaderboard("week", `circle:${bloq.id}`),
    enabled: open && !!bloq.id,
    retry: false,
  });
  const items = (data?.top || data?.items || []).slice(0, 3);
  const rankTint = (r) => (r === 1 ? "bg-amber-400/20 text-amber-300" : r === 2 ? "bg-slate-300/20 text-slate-200" : r === 3 ? "bg-orange-400/20 text-orange-300" : "bg-white/8 text-bloq-muted");
  const initials = (name) => ((name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?");

  return (
    <div className="mt-6" data-testid="bloq-supporters-section">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-bloq-muted">
        <Gift className="h-3.5 w-3.5 text-bloq-purple" /> Top supporters this week
      </div>
      {items.length === 0 ? (
        <div className="rounded-2xl border border-white/8 bg-bloq-s2 px-4 py-5 text-center text-xs text-bloq-muted" data-testid="bloq-supporters-empty">
          No supporters yet this week — send a tip in this bloq to be the first. 🎉
        </div>
      ) : (
        <div className="space-y-2" data-testid="bloq-supporters-list">
          {items.map((row, idx) => {
            const u = row.user || row;
            const rank = row.rank ?? idx + 1;
            return (
              <div key={rank} className="flex items-center gap-3 rounded-xl border border-white/8 bg-bloq-s2 px-3 py-2.5" data-testid={`bloq-supporter-${rank}`}>
                <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold", rankTint(rank))}>{rank}</span>
                {u.avatar_url ? (
                  <img src={u.avatar_url} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
                ) : (
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bloq-purple/20 text-[11px] font-bold text-bloq-purple">{initials(u.display_name || u.handle)}</span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-white">{u.display_name || `@${u.handle}`}</div>
                  {u.handle && <div className="truncate text-[11px] text-bloq-muted">@{u.handle}</div>}
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-bloq-mint">${(Number(row.amount || 0) / 100).toFixed(2)}</div>
                  <div className="text-[10px] uppercase tracking-wide text-bloq-muted">{row.count ?? 0} tips</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MonetizationSection({ bloq, invalidate, open }) {
  const membership = bloq.membership || null;
  const [enabled, setEnabled] = useState(!!membership?.enabled);
  const [price, setPrice] = useState(membership?.price_usd ?? 4.99);
  const [busy, setBusy] = useState(false);

  const { data: analytics, error: analyticsError } = useQuery({
    queryKey: ["bloq-analytics", bloq.id],
    queryFn: () => api.bloqAnalytics(bloq.id),
    enabled: open && !!bloq.id,
    retry: false,
  });
  const analyticsLocked = analyticsError?.response?.status === 429 || analyticsError?.response?.data?.detail?.code === "upgrade_required";

  const save = async () => {
    if (busy) return;
    if (enabled && (!Number(price) || Number(price) <= 0)) return toast.error("Set a price greater than $0");
    setBusy(true);
    try {
      await api.setBloqMembership(bloq.id, {
        enabled,
        price_usd: enabled ? Number(price) : null,
        interval: "month",
      });
      toast.success(enabled ? "Membership pricing saved" : "Paid membership disabled");
      invalidate();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" && d) || d?.message || "Couldn't update membership");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6" data-testid="bloq-monetization-section">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-bloq-muted">
        <DollarSign className="h-3.5 w-3.5 text-bloq-mint" /> Monetization
      </div>
      <div className="rounded-2xl border border-white/8 bg-bloq-s2 p-4">
        <button
          onClick={() => setEnabled((v) => !v)}
          className="flex w-full items-center justify-between"
          data-testid="bloq-membership-toggle"
        >
          <div className="text-left">
            <div className="text-sm font-medium">Paid membership</div>
            <div className="text-xs text-bloq-muted">Charge members a monthly fee to join</div>
          </div>
          {enabled ? <ToggleRight className="h-6 w-6 text-bloq-mint" /> : <ToggleLeft className="h-6 w-6 text-bloq-muted" />}
        </button>

        {enabled && (
          <div className="mt-3 space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-bloq-muted">Price (USD / month)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-bloq-muted">$</span>
              <Input
                type="number"
                min="1"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="h-11 rounded-xl border-white/12 bg-bloq-bg pl-7"
                data-testid="bloq-membership-price"
              />
            </div>
          </div>
        )}

        <Button
          onClick={save}
          disabled={busy}
          className="mt-3 w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90"
          data-testid="bloq-membership-save"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save membership"}
        </Button>
        <p className="mt-2 text-[11px] text-bloq-muted">Requires an approved creator application (Creator Hub).</p>
      </div>

      <div className="mt-3 rounded-2xl border border-white/8 bg-bloq-s2 p-4" data-testid="bloq-analytics-panel">
        <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-bloq-muted">
          <BarChart3 className="h-3.5 w-3.5 text-bloq-purple" /> Analytics
        </div>
        {analyticsLocked ? (
          <p className="text-xs text-bloq-muted" data-testid="bloq-analytics-locked">Owner analytics are a Circle Pro perk.</p>
        ) : analytics ? (
          <div className="grid grid-cols-3 gap-2.5" data-testid="bloq-analytics-data">
            <div><div className="text-[10px] uppercase tracking-wider text-bloq-muted">Members</div><div className="text-base font-bold">{analytics.member_count ?? analytics.members ?? bloq.member_count ?? 0}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-bloq-muted">Paid</div><div className="text-base font-bold text-bloq-mint">{analytics.paid_member_count ?? analytics.paid_members ?? 0}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-bloq-muted">MRR</div><div className="text-base font-bold">${(Number(analytics.mrr_usd ?? analytics.revenue_usd ?? 0)).toFixed(2)}</div></div>
          </div>
        ) : (
          <p className="text-xs text-bloq-muted">No analytics yet.</p>
        )}
      </div>
    </div>
  );
}


export function BloqInfoDrawer({ bloq, open, onOpenChange, onOpenRoom, onDeleted, replayTarget }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [busyMember, setBusyMember] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const { data: rec } = useQuery({
    queryKey: ["bloq-recordings", bloq?.id],
    queryFn: () => api.bloqRecordings(bloq.id),
    enabled: open && !!bloq?.id,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["bloq", bloq?.id] });
    qc.invalidateQueries({ queryKey: ["bloqs"] });
  };

  const autoTranslateMut = useMutation({
    mutationFn: () => api.toggleBloqAutoTranslate(bloq.id),
    onSuccess: (res) => { invalidate(); toast.success(res?.auto_translate ? "Auto-translate on" : "Auto-translate off"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't toggle auto-translate"),
  });

  const deleteBloqMut = useMutation({
    mutationFn: () => api.deleteBloq(bloq.id),
    onSuccess: () => {
      toast.success("BLOQ deleted");
      qc.invalidateQueries({ queryKey: ["bloqs"] });
      qc.invalidateQueries({ queryKey: ["chats"] });
      setConfirmDelete(false);
      setConfirmText("");
      onOpenChange(false);
      onDeleted?.(bloq.id);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't delete this bloq"),
  });

  if (!bloq) return null;
  const members = bloq.members || [];
  const recordings = rec?.recordings || [];
  const activeRoom = bloq.active_room;
  const isOwner = bloq.role === "owner";
  const isAdmin = bloq.role === "admin";

  const changeRole = async (memberId, role) => {
    setBusyMember(memberId);
    try {
      await api.setBloqMemberRole(bloq.id, memberId, role);
      toast.success("Role updated");
      invalidate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change role");
    } finally { setBusyMember(null); }
  };

  const kick = async (memberId) => {
    setBusyMember(memberId);
    try {
      await api.kickBloqMember(bloq.id, memberId);
      toast.success("Member removed");
      invalidate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove member");
    } finally { setBusyMember(null); }
  };

  const canManageMember = (m) => {
    if (m.id === user?.id || m.role === "owner") return false;
    if (isOwner) return true;
    if (isAdmin) return m.role === "member" || m.role === "mod";
    return false;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto border-white/10 bg-bloq-s1 text-white sm:max-w-md" data-testid="bloq-info-drawer">
        <SheetHeader className="text-left">
          <div className="flex items-center gap-3">
            <BloqEmoji emoji={bloq.emoji} icon={bloq.icon_b64} size="lg" />
            <div className="min-w-0 flex-1">
              <SheetTitle className="truncate font-heading text-xl text-white">{bloq.name}</SheetTitle>
              <SheetDescription className="text-bloq-muted">
                {bloq.member_count} member{bloq.member_count === 1 ? "" : "s"}{bloq.member_cap ? ` · cap ${bloq.member_cap}` : ""}
              </SheetDescription>
            </div>
            {isOwner && (
              <button onClick={() => setEditOpen(true)} className="flex items-center gap-1 rounded-full border border-white/12 px-3 py-1.5 text-xs font-semibold text-bloq-muted hover:text-white" data-testid="bloq-edit-btn">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
            )}
          </div>
        </SheetHeader>

        {bloq.description && <p className="mt-4 text-sm text-white/80">{bloq.description}</p>}
        <LinkChips links={bloq.links} testid="bloq-description-links" className="mt-2" />

        <div className="mt-4 grid grid-cols-3 gap-2.5">
          <InfoTile icon={ShieldCheck} label="Trust gate" value={bloq.gate?.min_score ? `≥ ${bloq.gate.min_score}` : "Open"} accent="text-bloq-mint" />
          <InfoTile icon={Lock} label="Cipher Lock" value={bloq.e2ee ? "On" : "Off"} accent={bloq.e2ee ? "text-bloq-mint" : "text-bloq-muted"} />
          <InfoTile icon={Film} label="Recordings" value={bloq.recordings_count ?? recordings.length ?? 0} accent="text-bloq-purple" />
        </div>

        {bloq.gate?.require_verified && (
          <div className="mt-2"><GatePill minScore={bloq.gate?.min_score} requireVerified={bloq.gate?.require_verified} /></div>
        )}

        {/* Auto-translate (per-user preference) */}
        <button
          onClick={() => autoTranslateMut.mutate()}
          disabled={autoTranslateMut.isPending}
          className="mt-4 flex w-full items-center gap-3 rounded-2xl border border-white/8 bg-bloq-s2 px-4 py-3 text-left disabled:opacity-60"
          data-testid="auto-translate-toggle"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-bloq-blue/15 text-bloq-blue"><Languages className="h-4 w-4" /></span>
          <div className="flex-1">
            <div className="text-sm font-medium">Auto-translate this bloq</div>
            <div className="text-xs text-bloq-muted">Translate messages into your language</div>
          </div>
          {autoTranslateMut.isPending ? <Loader2 className="h-5 w-5 animate-spin text-bloq-muted" /> : bloq.auto_translate ? <ToggleRight className="h-6 w-6 text-bloq-mint" /> : <ToggleLeft className="h-6 w-6 text-bloq-muted" />}
        </button>

        {isOwner && <MonetizationSection bloq={bloq} invalidate={invalidate} open={open} />}

        {isOwner && MICROTX_ENABLED && <TokenGateSection bloq={bloq} invalidate={invalidate} open={open} />}

        {MICROTX_ENABLED && <BloqBoostSection bloq={bloq} />}

        {MICROTX_ENABLED && <BloqSupportersSection bloq={bloq} open={open} />}

        {/* Live Rooms */}
        <div className="mt-6">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-bloq-muted"><Radio className="h-3.5 w-3.5" /> Live room</div>
          {activeRoom ? (
            <button onClick={onOpenRoom} className="flex w-full items-center gap-3 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-left transition-colors hover:bg-destructive/15" data-testid="drawer-open-room">
              <span className="flex items-center gap-1 rounded-full bg-destructive/20 px-2 py-0.5 text-[10px] font-bold uppercase text-destructive"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-destructive" /> Live</span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{activeRoom.title}</div>
                <div className="text-xs text-bloq-muted">{activeRoom.speaker_count} on stage · {activeRoom.listener_count} listening</div>
              </div>
            </button>
          ) : bloq.can_start_room ? (
            <button onClick={onOpenRoom} className="flex w-full items-center justify-center gap-2 rounded-2xl border border-bloq-mint/30 bg-bloq-mint/[0.06] py-3 text-sm font-bold text-bloq-mint transition-colors hover:bg-bloq-mint/10" data-testid="drawer-start-room">
              <Radio className="h-4 w-4" /> Start a room
            </button>
          ) : (
            <p className="text-xs text-bloq-muted">No live room right now.</p>
          )}
        </div>

        {/* Ticketed events (Lane B) */}
        <EventsSection bloq={bloq} isOwner={isOwner} open={open} />

        {/* Recordings */}
        {recordings.length > 0 && (
          <div className="mt-6">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-bloq-muted"><Film className="h-3.5 w-3.5" /> Recordings</div>
            <div className="space-y-2">
              {recordings.map((r, i) => (
                <RecordingRow key={r.id || i} rec={r} index={i} autoplay={replayTarget?.id === r.id ? replayTarget : null} />
              ))}
            </div>
            {rec?.retention_days != null && (
              <div className="mt-1.5 flex items-center gap-1 text-[11px] text-bloq-muted"><Clock className="h-3 w-3" /> Kept for {rec.retention_days} days</div>
            )}
          </div>
        )}

        {/* Members */}
        <div className="mt-6">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-bloq-muted"><Users className="h-3.5 w-3.5" /> Members</div>
          <div className="space-y-1">
            {members.map((m) => (
              <div key={m.id} className="flex items-center gap-3 rounded-2xl px-2 py-2 hover:bg-white/5" data-testid={`member-${m.id}`}>
                <Avatar user={m} size="sm" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate font-medium">{m.display_name}</span>
                    {m.verified && <VerifiedCheck size={13} />}
                    <TopConnectorCrown match={m} iconOnly />
                  </div>
                  <div className="text-xs text-bloq-muted">@{m.username}</div>
                </div>
                {m.score_band && <TrustBand band={m.score_band} />}
                {m.role && m.role !== "member" && (
                  <span className={cn("inline-flex items-center gap-0.5 rounded-md border px-1.5 py-0.5 text-[9px] font-bold uppercase", ROLE_STYLE[m.role] || "border-white/15 text-bloq-muted")}>
                    {m.role === "owner" && <Crown className="h-2.5 w-2.5" />} {m.role}
                  </span>
                )}
                {canManageMember(m) && (
                  <div className="flex shrink-0 items-center gap-1">
                    {isOwner && (
                      <select
                        value={m.role || "member"}
                        onChange={(e) => changeRole(m.id, e.target.value)}
                        disabled={busyMember === m.id}
                        className="rounded-lg border border-white/12 bg-bloq-bg px-1.5 py-1 text-[11px] text-white focus:border-bloq-mint focus:outline-none"
                        data-testid={`member-role-${m.id}`}
                      >
                        <option value="member">member</option>
                        <option value="mod">mod</option>
                        <option value="admin">admin</option>
                      </select>
                    )}
                    <button onClick={() => kick(m.id)} disabled={busyMember === m.id} className="rounded-lg p-1.5 text-bloq-muted hover:text-destructive disabled:opacity-40" data-testid={`kick-member-${m.id}`}>
                      {busyMember === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                )}
              </div>
            ))}
            {members.length === 0 && <p className="text-xs text-bloq-muted">Member list unavailable.</p>}
          </div>
        </div>

        {isOwner && (
          <div className="mt-6 rounded-2xl border border-destructive/25 bg-destructive/[0.05] p-4" data-testid="bloq-danger-zone">
            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-destructive">
              <Trash2 className="h-3.5 w-3.5" /> Danger zone
            </div>
            <p className="mb-3 text-xs text-bloq-muted">Deleting a bloq permanently removes its chat, members and recordings for everyone. This can't be undone.</p>
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(true)}
              className="w-full rounded-full border-destructive/40 bg-transparent font-semibold text-destructive hover:bg-destructive/10"
              data-testid="bloq-delete-btn"
            >
              <Trash2 className="mr-1.5 h-4 w-4" /> Delete this bloq
            </Button>
          </div>
        )}

        <Dialog open={confirmDelete} onOpenChange={(o) => { setConfirmDelete(o); if (!o) setConfirmText(""); }}>
          <DialogContent className="border-white/10 bg-bloq-s1 text-white" data-testid="bloq-delete-dialog">
            <DialogHeader><DialogTitle className="font-heading text-destructive">Delete “{bloq.name}”?</DialogTitle></DialogHeader>
            <DialogDescription className="text-sm text-bloq-muted">
              This permanently deletes the bloq, its chat history, membership and recordings for <span className="font-semibold text-white">everyone</span>. This can't be undone.
            </DialogDescription>
            <div className="mt-1 space-y-1.5">
              <label className="text-xs text-bloq-muted">Type <span className="font-semibold text-white">{bloq.name}</span> to confirm</label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={bloq.name}
                autoFocus
                className="border-white/12 bg-bloq-s2"
                data-testid="bloq-delete-input"
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" onClick={() => { setConfirmDelete(false); setConfirmText(""); }} className="rounded-full border-white/15 bg-transparent" data-testid="bloq-delete-cancel">Cancel</Button>
              <Button variant="destructive" onClick={() => deleteBloqMut.mutate()} disabled={deleteBloqMut.isPending || confirmText.trim() !== bloq.name} className="rounded-full font-bold" data-testid="bloq-delete-confirm">
                {deleteBloqMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete forever"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {isOwner && <EditBloqDialog bloq={bloq} open={editOpen} onOpenChange={setEditOpen} onSaved={invalidate} />}
      </SheetContent>
    </Sheet>
  );
}
